"use strict";
/** *******************************************************************************************************************
  Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
  
  Licensed under the Apache License, Version 2.0 (the "License").
  You may not use this file except in compliance with the License.
  You may obtain a copy of the License at
  
      http://www.apache.org/licenses/LICENSE-2.0
  
  Unless required by applicable law or agreed to in writing, software
  distributed under the License is distributed on an "AS IS" BASIS,
  WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
  See the License for the specific language governing permissions and
  limitations under the License.                                                                              *
 ******************************************************************************************************************** */
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    Object.defineProperty(o, k2, { enumerable: true, get: function() { return m[k]; } });
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || function (mod) {
    if (mod && mod.__esModule) return mod;
    var result = {};
    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
    __setModuleDefault(result, mod);
    return result;
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
const react_1 = __importStar(require("react"));
const styles_1 = require("@material-ui/core/styles");
const CircularProgress_1 = __importDefault(require("@material-ui/core/CircularProgress"));
const Typography_1 = __importDefault(require("@material-ui/core/Typography"));
const LinearProgress_1 = __importDefault(require("@material-ui/core/LinearProgress"));
const Box_1 = __importDefault(require("../../layouts/Box"));
const Grid_1 = __importDefault(require("../../layouts/Grid"));
const StatusIndicator_1 = __importDefault(require("../StatusIndicator"));
const Button_1 = __importDefault(require("../Button"));
const Heading_1 = __importDefault(require("../Heading"));
const Text_1 = __importDefault(require("../Text"));
const useStyles = styles_1.makeStyles((theme) => ({
    colorPrimary: {
        marginTop: '8px',
    },
    circularColorPrimary: {
        color: theme.palette.info.dark,
        position: 'absolute',
        left: 0,
    },
    circularColorBottom: {
        color: theme.palette.grey[theme.palette.type === 'light' ? 200 : 700],
    },
    barColorPrimary: {
        backgroundColor: theme.palette.info.dark,
    },
    label: {
        color: theme.palette.text.primary,
    },
    description: {
        padding: theme.spacing(0.3, 0),
        color: theme.palette.text.secondary,
    },
    body: {
        display: 'inline-flex',
    },
    icon: {
        paddingTop: '4px',
    },
    resultButton: {
        marginLeft: '10px',
    },
}));
const statusMapping = {
    error: 'negative',
    success: 'positive',
};
const LinearProgressComponent = (_a) => {
    var { value, displayValue } = _a, props = __rest(_a, ["value", "displayValue"]);
    const classes = useStyles();
    return (react_1.default.createElement(Grid_1.default, { container: true, spacing: 3 },
        react_1.default.createElement(Grid_1.default, { item: true, xs: value && displayValue ? 11 : 12 },
            react_1.default.createElement(LinearProgress_1.default, Object.assign({ variant: "determinate", value: value, classes: {
                    colorPrimary: classes.colorPrimary,
                    barColorPrimary: classes.barColorPrimary,
                } }, props))),
        displayValue && value && (react_1.default.createElement(Grid_1.default, { item: true, xs: 1 },
            react_1.default.createElement(Text_1.default, null,
                value,
                "%")))));
};
const CircularProgressWithLabel = (_a) => {
    var { value, displayValue } = _a, props = __rest(_a, ["value", "displayValue"]);
    const classes = useStyles();
    return (react_1.default.createElement(Box_1.default, { position: "relative", display: "inline-flex" },
        react_1.default.createElement(CircularProgress_1.default, Object.assign({ value: 100, variant: "determinate", classes: { colorPrimary: classes.circularColorBottom } }, props)),
        react_1.default.createElement(CircularProgress_1.default, Object.assign({ variant: "determinate", value: value, classes: { colorPrimary: classes.circularColorPrimary } }, props)),
        displayValue && (react_1.default.createElement(Box_1.default, { display: "flex", top: 0, bottom: 0, right: 0, left: 0, position: "absolute", alignItems: "center", justifyContent: "center" },
            react_1.default.createElement(Typography_1.default, { variant: "caption", component: "div", color: "textSecondary" }, `${Math.round(value)}%`)))));
};
/**
 * A progress bar is a horizontal progress-bar for indicating progress and activity.
 */
const ProgressBar = (_a) => {
    var { value, displayValue = true, status = 'in-progress', variant = 'linear', label, description, additionalInfo, resultText, resultButtonText, resultButtonClick } = _a, props = __rest(_a, ["value", "displayValue", "status", "variant", "label", "description", "additionalInfo", "resultText", "resultButtonText", "resultButtonClick"]);
    const classes = useStyles();
    const progressBody = (ProgressBarComponent) => {
        if (status === 'error' || status === 'success') {
            return (react_1.default.createElement("div", { className: classes.body },
                react_1.default.createElement("div", { className: classes.icon },
                    react_1.default.createElement(StatusIndicator_1.default, { statusType: statusMapping[status] }, resultText)),
                resultButtonText && (react_1.default.createElement("div", { className: classes.resultButton },
                    react_1.default.createElement(Button_1.default, { onClick: resultButtonClick }, resultButtonText)))));
        }
        return (react_1.default.createElement(ProgressBarComponent, Object.assign({ value: value || (value === 0 ? 0 : 100), displayValue: displayValue }, props, { "data-testid": `${testId}-indicator` })));
    };
    const ProgressBarComponent = react_1.useMemo(() => (variant === 'linear' ? LinearProgressComponent : CircularProgressWithLabel), [variant]);
    const testId = props['data-testid'] || 'progress-bar';
    return (react_1.default.createElement("div", { "data-testid": testId },
        label && react_1.default.createElement(Heading_1.default, { variant: "h3" }, label),
        description && (react_1.default.createElement(Typography_1.default, { className: classes.description, variant: "body1" }, description)),
        progressBody(ProgressBarComponent),
        additionalInfo && (react_1.default.createElement(Typography_1.default, { className: classes.description, variant: "body1" }, additionalInfo))));
};
exports.default = ProgressBar;
//# sourceMappingURL=index.js.map